/**
 * Customer International Configuration Composable
 *
 * Provides country-specific configurations for customer forms based on
 * the selected franchise's country.
 *
 * ARCHITECTURE (Single Responsibility):
 * - This composable owns PRESENTATION data: phone, address labels, postal code, currency, flags
 * - Document business rules (allowed_types, default_type, required) come from the BACKEND
 *   via GET /clientes/document_config?franquia_id=X (cached with CacheService)
 * - Document presentation data (mask, placeholder, label) is in DOCUMENT_PRESENTATION,
 *   indexed by document type (not country), supporting multiple types per country
 *
 * Usage:
 * import { useCustomerInternationalConfig } from '../composables/useCustomerInternationalConfig'
 * const { config, setCountryFromName, isBrazil } = useCustomerInternationalConfig()
 */

import { ref, computed } from 'vue';

/**
 * Document presentation data indexed by document TYPE (not country).
 * This is purely UI data: masks, placeholders, labels, flags.
 * The backend decides WHICH types are allowed per country.
 */
const DOCUMENT_PRESENTATION = {
  cpf:      { label: 'CPF',    mask: '999.999.999-99',       placeholder: '000.000.000-00',      flag: '🇧🇷' },
  cnpj:     { label: 'CNPJ',   mask: '99.999.999/9999-99',   placeholder: '00.000.000/0000-00',  flag: '🇧🇷' },
  rut:      { label: 'RUT',    mask: '99.999.999-*',          placeholder: '12.345.678-K',        flag: '🇨🇱' },
  dni:      { label: 'DNI',    mask: '99999999',              placeholder: '12345678',            flag: '🇦🇷' },
  ci:       { label: 'CI',     mask: '9999999999',            placeholder: '1234567890',          flag: '🇧🇴' },
  ssn:      { label: 'SSN',    mask: '999-99-9999',           placeholder: '123-45-6789',         flag: '🇺🇸' },
  nif:      { label: 'NIF',    mask: '999999999',             placeholder: '123456789',           flag: '🇵🇹' },
  nif_es:   { label: 'NIF/NIE', mask: null,                  placeholder: '12345678Z',           flag: '🇪🇸' },
  curp:     { label: 'CURP',   mask: '******************',    placeholder: 'ABCDE123456FGHIJKL01', flag: '🇲🇽' },
  dpi:      { label: 'DPI',    mask: '****************',      placeholder: '1234567890123456',    flag: '🇬🇹' },
  cedula:   { label: 'Cédula', mask: '9999999',               placeholder: '1234567',             flag: '🇵🇾' },
  cedula_ec: { label: 'Cédula', mask: '9999999999',            placeholder: '1234567890',           flag: '🇪🇨' },
  ruc:       { label: 'RUC',    mask: '9999999999999',          placeholder: '1234567890001',         flag: '🇪🇨' },
  passport: { label: 'Passport', mask: null,                  placeholder: 'AB123456',            flag: '🌐' },
};

/**
 * Country configurations for customer forms.
 * Contains ONLY presentation/UX data — no document business rules.
 * Document rules come from the backend API.
 *
 * ESTA TABELA PRECISA COBRIR TODO PAIS QUE O REGISTRO DE LOCALIDADES SERVE.
 * `config` abaixo cai em `COUNTRY_CONFIGS.BR` para qualquer chave ausente, e
 * esse fallback e SILENCIOSO: a franquia estrangeira passa a usar as regras
 * brasileiras sem nenhum aviso. Foi assim que o chamado 2026-0034 chegou —
 * Valladolid nao conseguia cadastrar cliente porque, sem a entrada `ES`, o
 * formulario aplicava `phoneAreaCodeRequired: true` (a regra do DDD) a um
 * telefone espanhol, que nao tem DDD. ES, GB, UY e AO estavam todos faltando;
 * o backend ja conhecia os quatro (DocumentPolicy::TYPES, FranchiseCountry::
 * SYNONYMS). A divergencia entre as duas tabelas e presa por
 * tests/unit/specs/composables/useCustomerInternationalConfig.test.ts.
 *
 * AR, BO, MX e EC ficam aqui por anteciparem mercados que o registro ainda
 * nao serve — sobra e inofensivo, falta nao e.
 */
const COUNTRY_CONFIGS = {
  BR: {
    name: 'Brasil',
    flag: '🇧🇷',
    // Postal code configuration
    hasPostalCode: true,
    postalCodeLabel: 'CEP',
    postalCodeMask: '99999-999',
    postalCodeApi: 'viacep',
    // Address labels
    streetLabel: 'Rua',
    neighborhoodLabel: 'Bairro',
    complementLabel: 'Complemento',
    // State registration (only Brazil)
    hasInscricaoEstadual: true,
    inscricaoEstadualLabel: 'Inscrição Estadual',
    // Phone configuration
    phoneDdi: '+55',
    phoneAreaCodeRequired: true,
    phoneAreaCodeLabel: 'DDD',
    phoneAreaCodeLength: 2,
    // Currency (future)
    currency: 'BRL',
    currencySymbol: 'R$',
    currencyLocale: 'pt-BR'
  },
  CL: {
    name: 'Chile',
    flag: '🇨🇱',
    hasPostalCode: false,
    postalCodeLabel: null,
    postalCodeMask: null,
    postalCodeApi: null,
    streetLabel: 'Calle',
    neighborhoodLabel: 'Comuna',
    complementLabel: 'Depto/Oficina',
    hasInscricaoEstadual: false,
    inscricaoEstadualLabel: null,
    phoneDdi: '+56',
    phoneAreaCodeRequired: false,
    phoneAreaCodeLabel: null,
    phoneAreaCodeLength: 0,
    currency: 'CLP',
    currencySymbol: '$',
    currencyLocale: 'es-CL'
  },
  US: {
    name: 'United States',
    flag: '🇺🇸',
    hasPostalCode: true,
    postalCodeLabel: 'ZIP Code',
    postalCodeMask: '99999',
    postalCodeApi: null,
    streetLabel: 'Street',
    neighborhoodLabel: 'City',
    complementLabel: 'Apt/Suite',
    hasInscricaoEstadual: false,
    inscricaoEstadualLabel: null,
    phoneDdi: '+1',
    phoneAreaCodeRequired: true,
    phoneAreaCodeLabel: 'Area Code',
    phoneAreaCodeLength: 3,
    currency: 'USD',
    currencySymbol: '$',
    currencyLocale: 'en-US'
  },
  AR: {
    name: 'Argentina',
    flag: '🇦🇷',
    hasPostalCode: true,
    postalCodeLabel: 'Código Postal',
    postalCodeMask: 'a9999aaa',
    postalCodeApi: null,
    streetLabel: 'Calle',
    neighborhoodLabel: 'Barrio',
    complementLabel: 'Piso/Depto',
    hasInscricaoEstadual: false,
    inscricaoEstadualLabel: null,
    phoneDdi: '+54',
    phoneAreaCodeRequired: false,
    phoneAreaCodeLabel: null,
    phoneAreaCodeLength: 0,
    currency: 'ARS',
    currencySymbol: '$',
    currencyLocale: 'es-AR'
  },
  BO: {
    name: 'Bolivia',
    flag: '🇧🇴',
    hasPostalCode: false,
    postalCodeLabel: null,
    postalCodeMask: null,
    postalCodeApi: null,
    streetLabel: 'Calle',
    neighborhoodLabel: 'Zona',
    complementLabel: 'Depto',
    hasInscricaoEstadual: false,
    inscricaoEstadualLabel: null,
    phoneDdi: '+591',
    phoneAreaCodeRequired: false,
    phoneAreaCodeLabel: null,
    phoneAreaCodeLength: 0,
    currency: 'BOB',
    currencySymbol: 'Bs',
    currencyLocale: 'es-BO'
  },
  PT: {
    name: 'Portugal',
    flag: '🇵🇹',
    hasPostalCode: true,
    postalCodeLabel: 'Código Postal',
    postalCodeMask: '9999-999',
    postalCodeApi: null,
    streetLabel: 'Rua',
    neighborhoodLabel: 'Freguesia',
    complementLabel: 'Complemento',
    hasInscricaoEstadual: false,
    inscricaoEstadualLabel: null,
    phoneDdi: '+351',
    phoneAreaCodeRequired: false,
    phoneAreaCodeLabel: null,
    phoneAreaCodeLength: 0,
    currency: 'EUR',
    currencySymbol: '€',
    currencyLocale: 'pt-PT'
  },
  MX: {
    name: 'México',
    flag: '🇲🇽',
    hasPostalCode: true,
    postalCodeLabel: 'Código Postal',
    postalCodeMask: '99999',
    postalCodeApi: null,
    streetLabel: 'Calle',
    neighborhoodLabel: 'Colonia',
    complementLabel: 'Depto',
    hasInscricaoEstadual: false,
    inscricaoEstadualLabel: null,
    phoneDdi: '+52',
    phoneAreaCodeRequired: false,
    phoneAreaCodeLabel: null,
    phoneAreaCodeLength: 0,
    currency: 'MXN',
    currencySymbol: '$',
    currencyLocale: 'es-MX'
  },
  GT: {
    name: 'Guatemala',
    flag: '🇬🇹',
    hasPostalCode: true,
    postalCodeLabel: 'Código Postal',
    postalCodeMask: '99999',
    postalCodeApi: null,
    streetLabel: 'Calle',
    neighborhoodLabel: 'Zona',
    complementLabel: 'Apto',
    hasInscricaoEstadual: false,
    inscricaoEstadualLabel: null,
    phoneDdi: '+502',
    phoneAreaCodeRequired: false,
    phoneAreaCodeLabel: null,
    phoneAreaCodeLength: 0,
    currency: 'GTQ',
    currencySymbol: 'Q',
    currencyLocale: 'es-GT'
  },
  PY: {
    name: 'Paraguay',
    flag: '🇵🇾',
    hasPostalCode: true,
    postalCodeLabel: 'Código Postal',
    postalCodeMask: '999999',
    postalCodeApi: null,
    streetLabel: 'Calle',
    neighborhoodLabel: 'Barrio',
    complementLabel: 'Depto',
    hasInscricaoEstadual: false,
    inscricaoEstadualLabel: null,
    phoneDdi: '+595',
    phoneAreaCodeRequired: false,
    phoneAreaCodeLabel: null,
    phoneAreaCodeLength: 0,
    currency: 'PYG',
    currencySymbol: '₲',
    currencyLocale: 'es-PY'
  },
  EC: {
    name: 'Ecuador',
    flag: '🇪🇨',
    hasPostalCode: true,
    postalCodeLabel: 'Código Postal',
    postalCodeMask: '999999',
    postalCodeApi: null,
    streetLabel: 'Calle',
    neighborhoodLabel: 'Barrio',
    complementLabel: 'Depto',
    hasInscricaoEstadual: false,
    inscricaoEstadualLabel: null,
    phoneDdi: '+593',
    phoneAreaCodeRequired: false,
    phoneAreaCodeLabel: null,
    phoneAreaCodeLength: 0,
    currency: 'USD',
    currencySymbol: '$',
    currencyLocale: 'es-EC'
  },
  ES: {
    name: 'España',
    flag: '🇪🇸',
    hasPostalCode: true,
    postalCodeLabel: 'Código Postal',
    postalCodeMask: '99999',
    postalCodeApi: null,
    streetLabel: 'Calle',
    neighborhoodLabel: 'Barrio',
    complementLabel: 'Piso/Puerta',
    hasInscricaoEstadual: false,
    inscricaoEstadualLabel: null,
    phoneDdi: '+34',
    phoneAreaCodeRequired: false,
    phoneAreaCodeLabel: null,
    phoneAreaCodeLength: 0,
    currency: 'EUR',
    currencySymbol: '€',
    currencyLocale: 'es-ES'
  },
  GB: {
    name: 'United Kingdom',
    flag: '🇬🇧',
    hasPostalCode: true,
    postalCodeLabel: 'Postcode',
    // O postcode britanico e alfanumerico e de tamanho variavel (M1 1AE,
    // SW1A 1AA): qualquer mascara fixa recusaria endereco valido.
    postalCodeMask: null,
    postalCodeApi: null,
    streetLabel: 'Street',
    neighborhoodLabel: 'Town/City',
    complementLabel: 'Flat/Unit',
    hasInscricaoEstadual: false,
    inscricaoEstadualLabel: null,
    phoneDdi: '+44',
    phoneAreaCodeRequired: false,
    phoneAreaCodeLabel: null,
    phoneAreaCodeLength: 0,
    currency: 'GBP',
    currencySymbol: '£',
    currencyLocale: 'en-GB'
  },
  UY: {
    name: 'Uruguay',
    flag: '🇺🇾',
    hasPostalCode: true,
    postalCodeLabel: 'Código Postal',
    postalCodeMask: '99999',
    postalCodeApi: null,
    streetLabel: 'Calle',
    neighborhoodLabel: 'Barrio',
    complementLabel: 'Apto',
    hasInscricaoEstadual: false,
    inscricaoEstadualLabel: null,
    phoneDdi: '+598',
    phoneAreaCodeRequired: false,
    phoneAreaCodeLabel: null,
    phoneAreaCodeLength: 0,
    currency: 'UYU',
    currencySymbol: '$',
    currencyLocale: 'es-UY'
  },
  AO: {
    name: 'Angola',
    flag: '🇦🇴',
    hasPostalCode: false,
    postalCodeLabel: null,
    postalCodeMask: null,
    postalCodeApi: null,
    streetLabel: 'Rua',
    neighborhoodLabel: 'Bairro',
    complementLabel: 'Complemento',
    hasInscricaoEstadual: false,
    inscricaoEstadualLabel: null,
    phoneDdi: '+244',
    phoneAreaCodeRequired: false,
    phoneAreaCodeLabel: null,
    phoneAreaCodeLength: 0,
    currency: 'AOA',
    currencySymbol: 'Kz',
    currencyLocale: 'pt-AO'
  }
};

/**
 * Mapping from country names (in various languages) to country codes
 */
const COUNTRY_NAME_TO_CODE = {
  'Brasil': 'BR',
  'Brazil': 'BR',
  'Chile': 'CL',
  'Argentina': 'AR',
  'Bolivia': 'BO',
  'Bolívia': 'BO',
  'Estados Unidos': 'US',
  'USA': 'US',
  'United States': 'US',
  'Portugal': 'PT',
  'México': 'MX',
  'Mexico': 'MX',
  'Guatemala': 'GT',
  'Paraguai': 'PY',
  'Paraguay': 'PY',
  'Ecuador': 'EC',
  'Equador': 'EC',
  'España': 'ES',
  'Espanha': 'ES',
  'Spain': 'ES',
  'United Kingdom': 'GB',
  'Reino Unido': 'GB',
  'Uruguay': 'UY',
  'Uruguai': 'UY',
  'Angola': 'AO'
};

/**
 * Main composable for customer international configuration
 *
 * @returns {Object} Composable with:
 *   - currentCountry: ref with current country code
 *   - config: computed with current country configuration (presentation only)
 *   - documentConfig: ref with API-driven document config (allowed_types, default_type, required)
 *   - currentDocumentPresentation: computed presentation for current document type
 *   - setCountry: function to set country by code
 *   - setCountryFromName: function to set country by name
 *   - setDocumentConfig: function to set document config from API response
 *   - isBrazil: computed boolean for Brazil
 *   - isChile: computed boolean for Chile
 */
export function useCustomerInternationalConfig() {
  // Current country code (defaults to Brazil)
  const currentCountry = ref('BR');

  // Document config from backend API (source of truth for business rules)
  const documentConfig = ref({
    allowed_types: ['cpf', 'cnpj'],
    default_type: 'cpf',
    required: false,
    country: 'Brasil'
  });

  // Currently selected document type (user can pick from allowed_types)
  const selectedDocumentType = ref('cpf');

  /**
   * Get configuration for current country (presentation only — no document rules)
   */
  const config = computed(() =>
    (COUNTRY_CONFIGS as any)[currentCountry.value] || COUNTRY_CONFIGS.BR
  );

  /**
   * Get presentation data for the currently selected document type
   */
  const currentDocumentPresentation = computed(() =>
    (DOCUMENT_PRESENTATION as any)[selectedDocumentType.value] || DOCUMENT_PRESENTATION.cpf
  );

  /**
   * Whether the user can choose between multiple document types
   */
  const hasMultipleDocumentTypes = computed(() =>
    documentConfig.value.allowed_types.length > 1
  );

  /**
   * Get presentation options for all allowed document types (for select/radio UI)
   */
  const allowedDocumentOptions = computed(() =>
    documentConfig.value.allowed_types.map(type => ({
      type,
      ...((DOCUMENT_PRESENTATION as any)[type] || DOCUMENT_PRESENTATION.cpf)
    }))
  );

  /**
   * Set country by ISO code (BR, CL, AR, BO, etc.)
   */
  const setCountry = (countryCode: any) => {
    currentCountry.value = (COUNTRY_CONFIGS as any)[countryCode] ? countryCode : 'BR';
  };

  /**
   * Set country by name OR by ISO2 code — tolerant of both franquias.pais
   * storage states during the S1b migration (pre: free-text name, post: ISO
   * code). An ISO2-shaped value that matches a known country wins outright;
   * otherwise falls back to the legacy name lookup.
   */
  const setCountryFromName = (countryName: any) => {
    if (typeof countryName === 'string' && /^[A-Z]{2}$/.test(countryName) && (COUNTRY_CONFIGS as any)[countryName]) {
      setCountry(countryName);
      return;
    }
    const code = (COUNTRY_NAME_TO_CODE as any)[countryName];
    setCountry(code || 'BR');
  };

  /**
   * Set document config from backend API response.
   * Call this after fetching GET /clientes/document_config?franquia_id=X
   *
   * @param {Object} apiConfig - { document_types, default_document_type, document_required, country }
   */
  const setDocumentConfig = (apiConfig: any) => {
    documentConfig.value = {
      allowed_types: apiConfig.document_types || ['cpf', 'cnpj'],
      default_type: apiConfig.default_document_type || 'cpf',
      required: apiConfig.document_required || false,
      country: apiConfig.country || 'Brasil'
    };
    // Auto-select default type
    selectedDocumentType.value = documentConfig.value.default_type;
  };

  /**
   * Set the selected document type (when user picks from allowed types)
   * @param {string} type - Document type (cpf, cnpj, rut, etc.)
   * @param {boolean} isAutoDetected - If true, bypass allowed_types check (for existing data)
   */
  const setSelectedDocumentType = (type: any, isAutoDetected = false) => {
    // Se for auto-detecção de dados existentes, permitir mesmo que não esteja em allowed_types
    if (isAutoDetected || documentConfig.value.allowed_types.includes(type)) {
      selectedDocumentType.value = type;

      // Se foi auto-detectado e não está na lista, adicionar para manter consistência
      if (isAutoDetected && !documentConfig.value.allowed_types.includes(type)) {
        documentConfig.value.allowed_types.push(type);
      }
    }
  };

  const isBrazil = computed(() => currentCountry.value === 'BR');
  const isChile = computed(() => currentCountry.value === 'CL');

  const phoneCountryCode = computed(() => {
    const map: any = {
      BR: 'br', CL: 'cl', AR: 'ar', BO: 'bo',
      US: 'us', PT: 'pt', MX: 'mx', GT: 'gt', PY: 'py', EC: 'ec',
      ES: 'es', GB: 'gb', UY: 'uy', AO: 'ao'
    };
    return (map as any)[currentCountry.value] || 'br';
  });

  return {
    currentCountry,
    config,
    documentConfig,
    selectedDocumentType,
    currentDocumentPresentation,
    hasMultipleDocumentTypes,
    allowedDocumentOptions,
    setCountry,
    setCountryFromName,
    setDocumentConfig,
    setSelectedDocumentType,
    isBrazil,
    isChile,
    phoneCountryCode
  };
}

/**
 * Export for direct access
 */
export { COUNTRY_CONFIGS, COUNTRY_NAME_TO_CODE, DOCUMENT_PRESENTATION };

export default useCustomerInternationalConfig;
